import React from 'react';
import { LearnerSlideModalPanel } from './LearnerSlideModalPanel';

export type LearnerSlideModalAlign = 'center' | 'bottom';

export interface LearnerSlideModalShellProps {
  mounted: boolean;
  visible: boolean;
  panelRef: React.RefObject<HTMLDivElement | null>;
  onBackdropClick: () => void;
  onPanelTransitionEnd: (e: React.TransitionEvent<HTMLDivElement>) => void;
  panelClassName: string;
  ariaLabelledBy?: string;
  zIndexClass?: string;
  /** Centered standalone modal (default) or bottom sheet. */
  align?: LearnerSlideModalAlign;
  /** Render children directly in the align container (for stacked phase panels). */
  bareContainer?: boolean;
  children: React.ReactNode;
}

export const LearnerSlideModalShell: React.FC<LearnerSlideModalShellProps> = ({
  mounted,
  visible,
  panelRef,
  onBackdropClick,
  onPanelTransitionEnd,
  panelClassName,
  ariaLabelledBy,
  zIndexClass = 'z-[200]',
  align = 'center',
  bareContainer = false,
  children,
}) => {
  if (!mounted) return null;

  const alignClass = align === 'bottom' ? 'items-end' : 'items-center';

  return (
    <div
      className={`learner-slide-modal-root fixed inset-0 ${zIndexClass} py-6 sm:py-10`}
      role="presentation"
    >
      <div
        className={`keyboard-slide-modal-backdrop fixed inset-0 bg-black/45 backdrop-blur-[2px] dark:bg-black/55${
          visible ? ' is-visible' : ''
        }`}
        aria-hidden="true"
        onClick={onBackdropClick}
      />
      <div className={`flex min-h-full ${alignClass} justify-center px-3 sm:px-6`}>
        {bareContainer ? (
          <div className="relative w-full max-w-[min(56rem,calc(100vw-1.5rem))]">{children}</div>
        ) : (
          <LearnerSlideModalPanel
            visible={visible}
            panelRef={panelRef}
            onTransitionEnd={onPanelTransitionEnd}
            panelClassName={panelClassName}
            {...(ariaLabelledBy ? { ariaLabelledBy } : {})}
          >
            {children}
          </LearnerSlideModalPanel>
        )}
      </div>
    </div>
  );
};

export interface LearnerSlidePhaseLayerProps {
  visible: boolean;
  className?: string;
  children: React.ReactNode;
}

/** @deprecated Prefer stacked LearnerSlideModalPanel for phase hand-offs. */
export const LearnerSlidePhaseLayer: React.FC<LearnerSlidePhaseLayerProps> = ({
  visible,
  className = '',
  children,
}) => (
  <div
    className={`learner-slide-phase-panel absolute inset-0 flex min-h-0 flex-col overflow-hidden${
      visible ? ' is-visible' : ''
    } ${className}`}
  >
    <div className="flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-y-contain px-4 py-4 sm:px-6 sm:py-6">
      {children}
    </div>
  </div>
);

export { LearnerSlideModalPanel } from './LearnerSlideModalPanel';

export default LearnerSlideModalShell;
